import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import aviso, { textoDoErro } from '../lib/aviso';
import {
    LuSearch, LuEye, LuPencil, LuCamera, LuPalette, LuInfo, LuArrowRight,
    LuChevronLeft, LuChevronRight, LuSlidersHorizontal,
} from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import RecordPanel, { PanelSection } from '../components/RecordPanel';
import { ListRow, PerPage, RowAction, RowActions, ListCard, ListCards } from '../components/ListKit';
import { useCelular } from '../hooks/useCelular';
import '../components/ListKit.css';
import LogoCropModal from '../components/LogoCropModal';
import EscolherTrilhoModal from '../components/EscolherTrilhoModal';
import { ParametrosEmpresaService, type ParametrosEmpresa } from '../services/ParametrosEmpresaService';
import { ModeloTarefaService, type ModeloTarefa } from '../services/ModeloTarefaService';
import { TRILHOS_PRONTOS } from '../data/trilhosProntos';
import { layoutPadrao } from '../components/TrilhoCanvas';
import GerenciarEmpresaService, {
    DEFAULT_DIAS_UTEIS, getMidiaUrl,
    type DiasUteis, type DadosGeraisEmpresa,
} from '../services/GerenciarEmpresaService';
import './GerenciarEmpresaPage.css';

const POR_PAGINA = [25, 50, 100];
type Modo = 'ver' | 'editar';
type Aba = 'dados' | 'parametros';

const LOGO_MIN_DIMENSION = 64;
const LOGO_MAX_DIMENSION = 2000;
const LOGO_MIN_ASPECT = 1;
const LOGO_MAX_ASPECT = 4;

const readImageDimensions = (arquivo: File): Promise<{ width: number; height: number }> =>
    new Promise((resolve, reject) => {
        const url = URL.createObjectURL(arquivo);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve({ width: img.naturalWidth, height: img.naturalHeight }); };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Não foi possível ler a imagem selecionada.')); };
        img.src = url;
    });

const DIAS: { key: keyof DiasUteis; abbr: string; nome: string }[] = [
    { key: 'segunda', abbr: 'SEG', nome: 'Segunda-feira' },
    { key: 'terca', abbr: 'TER', nome: 'Terça-feira' },
    { key: 'quarta', abbr: 'QUA', nome: 'Quarta-feira' },
    { key: 'quinta', abbr: 'QUI', nome: 'Quinta-feira' },
    { key: 'sexta', abbr: 'SEX', nome: 'Sexta-feira' },
    { key: 'sabado', abbr: 'SÁB', nome: 'Sábado' },
    { key: 'domingo', abbr: 'DOM', nome: 'Domingo' },
];

/* O CNPJ vinha cru na tela (`62570381000100`). Formata para ler, e guarda só
   os dígitos, que é o que a API recebe. */
const soDigitos = (v: string) => (v || '').replace(/\D/g, '').slice(0, 14);

const comMascaraCnpj = (v: string) => {
    const d = soDigitos(v);
    if (!d) return '';
    return d
        .replace(/^(\d{2})(\d)/, '$1.$2')
        .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/\.(\d{3})(\d)/, '.$1/$2')
        .replace(/(\d{4})(\d)/, '$1-$2');
};

const iniciais = (nome: string) => (nome || '').trim().split(/\s+/).slice(0, 2)
    .map(p => p[0]?.toUpperCase() ?? '').join('') || '?';

/* "Segunda a sexta" quando for a semana corrida, senão a lista. Dizer
   "5 dias" sem dizer quais não ajuda ninguém. */
const resumoDias = (dias: DiasUteis) => {
    const ativos = DIAS.filter(d => dias[d.key]);
    if (ativos.length === 0) return 'Nenhum dia selecionado';
    if (ativos.length === 7) return 'Todos os dias';
    const idx = ativos.map(a => DIAS.findIndex(d => d.key === a.key));
    const corrido = idx.every((n, i) => i === 0 || n === idx[i - 1] + 1);
    if (corrido && ativos.length > 2) {
        const curto = (n: string) => n.replace('-feira', '');
        return `${curto(ativos[0].nome)} a ${curto(ativos[ativos.length - 1].nome).toLowerCase()}`;
    }
    return ativos.map(a => a.abbr).join(', ');
};

interface Rascunho { nomeEmpresa: string; ramoAtividade: string; cnpj: string }
interface RascunhoParametros { usaTarefasPadrao: boolean; mostrarNomeDoAtendente: boolean }

const GerenciarEmpresaPage = () => {
    const celular = useCelular();
    const [empresas, setEmpresas] = useState<DadosGeraisEmpresa[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [busca, setBusca] = useState('');
    const [pagina, setPagina] = useState(1);
    const [porPagina, setPorPagina] = useState(25);

    /* Os dias são um conjunto só na API (`/DiasUteis`, sem empresa no
       caminho), então moram fora da lista. Quando o endpoint passar a ser por
       empresa, eles viram campo de `DadosGeraisEmpresa` e o painel lê do
       registro aberto em vez deste estado. */
    const [dias, setDias] = useState<DiasUteis>(DEFAULT_DIAS_UTEIS);

    /* Quem está aberto: índice, porque a API não devolve id de empresa. */
    const [abertoIdx, setAbertoIdx] = useState<number | null>(null);
    const [modo, setModo] = useState<Modo>('ver');
    const [rascunho, setRascunho] = useState<Rascunho>({ nomeEmpresa: '', ramoAtividade: '', cnpj: '' });
    const [diasRascunho, setDiasRascunho] = useState<DiasUteis>(DEFAULT_DIAS_UTEIS);
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');

    /* ─── Aba Parâmetros (docs/propostas/tarefas-padrao.html, tela 1) ─── */
    const [aba, setAba] = useState<Aba>('dados');
    const [parametros, setParametros] = useState<ParametrosEmpresa | null>(null);
    const [salvandoParam, setSalvandoParam] = useState(false);
    const [bibliotecaAberta, setBibliotecaAberta] = useState(false);

    const [logoFalhou, setLogoFalhou] = useState(false);
    const [enviandoLogo, setEnviandoLogo] = useState(false);
    const [arquivoPendente, setArquivoPendente] = useState<File | null>(null);
    const refArquivo = useRef<HTMLInputElement>(null);
    const [gravandoCor, setGravandoCor] = useState(false);
    const refCor = useRef<HTMLInputElement>(null);

    const carregar = useCallback(async () => {
        setIsLoading(true);
        try {
            const lista = await GerenciarEmpresaService.listarEmpresas();
            setEmpresas(lista);
        } catch (e) {
            aviso.erro(textoDoErro(e, 'Não foi possível carregar os dados da empresa.'));
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        carregar();
        GerenciarEmpresaService.getDiasUteis()
            .then(setDias)
            .catch(e => aviso.erro(textoDoErro(e, 'Não foi possível carregar os dias de funcionamento.')));
        ParametrosEmpresaService.ler()
            .then(setParametros)
            .catch(e => aviso.erro(textoDoErro(e, 'Não foi possível carregar os parâmetros da empresa.')));
    }, [carregar]);

    useEffect(() => { setPagina(1); }, [busca, porPagina]);

    const filtradas = useMemo(() => {
        const t = busca.trim().toLowerCase();
        if (!t) return empresas;
        return empresas.filter(e =>
            (e.nomeEmpresa || '').toLowerCase().includes(t)
            || (e.ramoAtividade || '').toLowerCase().includes(t)
            || soDigitos(e.cnpj).includes(soDigitos(t)));
    }, [empresas, busca]);

    const totalPaginas = Math.max(1, Math.ceil(filtradas.length / porPagina));
    const paginaAtual = Math.min(pagina, totalPaginas);
    const inicio = (paginaAtual - 1) * porPagina;
    const visiveis = filtradas.slice(inicio, inicio + porPagina);

    const aberta = abertoIdx === null ? null : empresas[abertoIdx] ?? null;

    const abrirVer = (e: DadosGeraisEmpresa) => {
        setAbertoIdx(empresas.indexOf(e));
        setModo('ver'); setErro(''); setLogoFalhou(false);
        setAba('dados');
    };

    const abrirEdicao = (e: DadosGeraisEmpresa) => {
        setAbertoIdx(empresas.indexOf(e));
        setRascunho({
            nomeEmpresa: e.nomeEmpresa ?? '',
            ramoAtividade: e.ramoAtividade ?? '',
            cnpj: e.cnpj ?? '',
        });
        setDiasRascunho(dias);
        setModo('editar'); setErro('');
    };

    const fechar = () => { setAbertoIdx(null); setErro(''); };

    /* ─── Parâmetros ─────────────────────────────────────────── */

    const nomeTrilho = TRILHOS_PRONTOS.find(t => t.chave === parametros?.chaveTrilhoBiblioteca)?.nome
        ?? (parametros?.chaveTrilhoBiblioteca === 'zero' ? 'montado do zero' : parametros?.chaveTrilhoBiblioteca ?? null);

    /* Sem Editar nem Salvar: o interruptor grava no clique. Ele já vira na
       hora e volta se a API recusar. */
    const gravarParametros = async (dados: RascunhoParametros) => {
        const anterior = parametros;
        if (anterior) setParametros({ ...anterior, ...dados });
        setSalvandoParam(true);
        try {
            const r = await ParametrosEmpresaService.gravar(dados);
            if (!r.sucesso) { setParametros(anterior); aviso.erro(r.mensagem || 'Não foi possível gravar os parâmetros.'); return; }
            setParametros(r.dados);
            aviso.ok(r.mensagem || 'Parâmetros gravados.');
        } catch (e) {
            setParametros(anterior);
            aviso.erro(textoDoErro(e, 'Não foi possível gravar os parâmetros.'));
        } finally {
            setSalvandoParam(false);
        }
    };

    const alternarParametro = async (mudanca: Partial<RascunhoParametros>) => {
        if (!parametros) return;
        const dados: RascunhoParametros = {
            usaTarefasPadrao: parametros.usaTarefasPadrao,
            mostrarNomeDoAtendente: parametros.mostrarNomeDoAtendente,
            ...mudanca,
        };
        // Ligar sem trilho gravaria no vazio: a biblioteca vem antes, e o trilho escolhido liga o parâmetro.
        if (dados.usaTarefasPadrao && !parametros.usaTarefasPadrao && !parametros.temTrilho) { setBibliotecaAberta(true); return; }
        await gravarParametros(dados);
    };

    /* Desde o quadro (docs/propostas/tarefas-padrao-quadro.html) a empresa tem
       VÁRIOS trilhos, com um ativo. Escolher aqui deixou de substituir o trilho:
       cria um trilho com o nome do pronto e o ativa. O resto da tela não muda. */
    const escolherTrilho = async (chave: string, etapas: ModeloTarefa[]) => {
        const pronto = TRILHOS_PRONTOS.find(t => t.chave === chave);
        const r = await ModeloTarefaService.criarConjunto({
            nome: pronto?.nome ?? 'Trilho da empresa',
            chaveBiblioteca: chave === 'zero' ? null : chave,
            layout: JSON.stringify(layoutPadrao()),
            etapas,
        });
        if (!r.sucesso) { aviso.erro(r.mensagem || 'Não foi possível criar o trilho.'); return; }
        if (etapas.length > 0) {
            const a = await ModeloTarefaService.ativarConjunto(r.dados.conjuntoId);
            if (!a.sucesso) { aviso.erro(a.mensagem || 'Não foi possível ativar o trilho.'); return; }
        }
        setBibliotecaAberta(false);
        await gravarParametros({ usaTarefasPadrao: true, mostrarNomeDoAtendente: parametros?.mostrarNomeDoAtendente ?? true });
    };

    /* ─── Logo ───────────────────────────────────────────────── */

    const escolherArquivo = (e: React.ChangeEvent<HTMLInputElement>) => {
        const arquivo = e.target.files?.[0];
        if (refArquivo.current) refArquivo.current.value = '';
        if (arquivo) setArquivoPendente(arquivo);
    };

    const confirmarCorte = async (cortado: File) => {
        setArquivoPendente(null);
        try {
            const { width, height } = await readImageDimensions(cortado);
            if (width < LOGO_MIN_DIMENSION || height < LOGO_MIN_DIMENSION) {
                aviso.atencao(`A área cortada deve ter no mínimo ${LOGO_MIN_DIMENSION}x${LOGO_MIN_DIMENSION}px.`);
                return;
            }
            if (width > LOGO_MAX_DIMENSION || height > LOGO_MAX_DIMENSION) {
                aviso.atencao(`A área cortada deve ter no máximo ${LOGO_MAX_DIMENSION}x${LOGO_MAX_DIMENSION}px.`);
                return;
            }
        } catch (e) {
            aviso.erro(textoDoErro(e, 'Não foi possível validar a imagem cortada.'));
            return;
        }

        setEnviandoLogo(true);
        try {
            const { caminhoRelativo } = await GerenciarEmpresaService.updateLogo(cortado);
            setEmpresas(lista => lista.map((e, i) =>
                i === abertoIdx ? { ...e, pathLogo: caminhoRelativo } : e));
            setLogoFalhou(false);
            aviso.ok('Logo atualizada.');
        } catch (e) {
            aviso.erro(textoDoErro(e, 'Não foi possível atualizar a logo.'));
        } finally {
            setEnviandoLogo(false);
        }
    };

    /* ─── Cor da marca ───────────────────────────────────────── */
    /* Mesma lógica do logo: vive no modo ver e grava na hora, com endpoint
       próprio. O seletor nativo dispara `input` a cada arrasto; o que grava é
       o `change`, que só vem quando a pessoa fecha o seletor. */

    const gravarCor = useCallback(async (cor: string | null) => {
        if (abertoIdx === null) return;
        setGravandoCor(true);
        try {
            const gravada = await GerenciarEmpresaService.atualizarCorMarca(cor);
            setEmpresas(lista => lista.map((e, i) => i === abertoIdx ? { ...e, corMarca: gravada } : e));
            aviso.ok(gravada ? 'Cor da marca atualizada.' : 'Cor da marca removida.');
        } catch (e) {
            aviso.erro(textoDoErro(e, 'Não foi possível gravar a cor da marca.'));
        } finally {
            setGravandoCor(false);
        }
    }, [abertoIdx]);

    useEffect(() => {
        const el = refCor.current;
        if (!el) return;
        const aoMudar = () => gravarCor(el.value);
        el.addEventListener('change', aoMudar);
        return () => el.removeEventListener('change', aoMudar);
    });

    /* ─── Salvar ─────────────────────────────────────────────── */

    const alternarDia = (dia: keyof DiasUteis) =>
        setDiasRascunho(d => ({ ...d, [dia]: !d[dia] }));

    const salvar = async () => {
        if (abertoIdx === null || !aberta) return;
        const nome = rascunho.nomeEmpresa.trim();
        if (!nome) { setErro('Informe o nome da empresa.'); return; }

        const mudouDados = nome !== aberta.nomeEmpresa
            || rascunho.ramoAtividade.trim() !== aberta.ramoAtividade
            || soDigitos(rascunho.cnpj) !== soDigitos(aberta.cnpj);
        const mudouDias = DIAS.some(d => diasRascunho[d.key] !== dias[d.key]);

        if (!mudouDados && !mudouDias) { setModo('ver'); return; }

        setSalvando(true);
        try {
            if (mudouDados) {
                await GerenciarEmpresaService.atualizarDadosGerais({
                    nomeEmpresa: nome,
                    ramoAtividade: rascunho.ramoAtividade.trim(),
                    cnpj: soDigitos(rascunho.cnpj), // a API recebe só os dígitos
                });
                setEmpresas(lista => lista.map((e, i) => i === abertoIdx
                    ? { ...e, nomeEmpresa: nome, ramoAtividade: rascunho.ramoAtividade.trim(), cnpj: soDigitos(rascunho.cnpj) }
                    : e));
            }
            if (mudouDias) {
                await GerenciarEmpresaService.updateDiasUteis(diasRascunho);
                setDias(diasRascunho);
            }
            aviso.ok('Empresa atualizada.');
            setModo('ver');
        } catch (e) {
            aviso.erro(textoDoErro(e, 'Não foi possível salvar as alterações.'));
        } finally {
            setSalvando(false);
        }
    };

    /* ─── Render ─────────────────────────────────────────────── */

    const logoUrl = aberta?.pathLogo ? getMidiaUrl(aberta.pathLogo) : null;
    const mostrarLogo = !!logoUrl && !logoFalhou;
    const ativos = DIAS.filter(d => (modo === 'editar' ? diasRascunho : dias)[d.key]);

    const COLUNAS = '2.2fr 1.2fr 1.3fr 1.4fr 78px';

    /* O aviso de "nada aqui" e o rodapé de paginação servem à tabela (no
       computador) e aos cartões (no celular) — por isso saem do meio do JSX. */
    const listaVazia = (
        <div className="lk-blank">
            <div className="lk-blank-title">
                {empresas.length === 0 ? 'Nenhuma empresa carregada' : 'Nada com esse termo'}
            </div>
            <p className="lk-blank-text">
                {empresas.length === 0
                    ? 'Não foi possível ler os dados da sua empresa. Recarregue a página ou tente de novo em instantes.'
                    : 'Ajuste a busca para ver as outras.'}
            </p>
        </div>
    );

    const rodape = (
        <div className="lk-foot">
            <PerPage value={porPagina} options={POR_PAGINA} onChange={setPorPagina} />
            <span className="lk-range">
                {inicio + 1}–{Math.min(inicio + porPagina, filtradas.length)} de {filtradas.length}
            </span>
            <span className="lk-pages">
                <button type="button" className="lk-page" aria-label="Página anterior"
                    disabled={paginaAtual === 1} onClick={() => setPagina(p => Math.max(1, p - 1))}>
                    <LuChevronLeft size={13} />
                </button>
                <span className="lk-page is-current">{paginaAtual}</span>
                <span className="lk-muted">de {totalPaginas}</span>
                <button type="button" className="lk-page" aria-label="Próxima página"
                    disabled={paginaAtual === totalPaginas} onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}>
                    <LuChevronRight size={13} />
                </button>
            </span>
        </div>
    );

    return (
        <div className="page-shell lk-screen">
            <PageHeader
                title="Minhas empresas"
                subtitle="Identidade, CNPJ e dias de funcionamento de cada empresa que você administra."
            />

            <div className="page-body lk-fill">
                <div className="lk-filters">
                    {/* Busca só quando há o que buscar: com uma empresa só, o campo
                        é um controle que não faz nada. */}
                    {empresas.length > 1 && (
                        <label className="lk-search">
                            <LuSearch size={14} />
                            <input type="search" placeholder="Buscar empresa…"
                                value={busca} onChange={e => setBusca(e.target.value)} />
                        </label>
                    )}
                    <span className={`lk-count ${empresas.length > 1 ? '' : 'ge-count-solo'}`}>
                        {empresas.length} {empresas.length === 1 ? 'empresa' : 'empresas'}
                    </span>
                </div>

                {/* Celular: a grade de cinco colunas não cabe em 358px, então a
                    linha vira cartão — a empresa, como ela se identifica e os
                    dias em que funciona. O resto fica no painel. */}
                {celular ? (
                    <>
                        <ListCards>
                            {isLoading
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map((e, i) => (
                                    <ListCard
                                        key={`${e.cnpj || e.nomeEmpresa || 'empresa'}-${i}`}
                                        avatar={<span className="lk-ava" aria-hidden="true">{iniciais(e.nomeEmpresa)}</span>}
                                        titulo={e.nomeEmpresa || '—'}
                                        apoio={e.ramoAtividade || (e.cnpj ? comMascaraCnpj(e.cnpj) : 'sem ramo de atividade')}
                                        estado={resumoDias(dias)}
                                        selected={aberta === e}
                                        label={`Abrir ${e.nomeEmpresa || 'empresa'}`}
                                        onOpen={() => abrirVer(e)}
                                    />
                                ))}
                        </ListCards>
                        {!isLoading && filtradas.length === 0 && listaVazia}
                        {!isLoading && filtradas.length > porPagina && rodape}
                    </>
                ) : (
                <div className="lk-table">
                    <div className="lk-scroll">
                        <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                            <div className="lk-head">Empresa</div>
                            <div className="lk-head">Ramo de atividade</div>
                            <div className="lk-head">CNPJ</div>
                            <div className="lk-head">Funcionamento</div>
                            <div className="lk-head lk-head-acts">Ações</div>
                        </div>
                        <div className="lk-body">
                            {isLoading
                                ? Array.from({ length: 3 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map((e, i) => {
                                    const url = e.pathLogo ? getMidiaUrl(e.pathLogo) : null;
                                    return (
                                        <ListRow key={`${e.cnpj || e.nomeEmpresa || 'empresa'}-${i}`}
                                            columns={COLUNAS}
                                            selected={aberta === e}>
                                            <span className="lk-cell lk-strong">
                                                <span className="ge-emp">
                                                    {url
                                                        ? <img className="ge-marca" src={url} alt="" />
                                                        : <span className="ge-marca is-vazia" aria-hidden="true">
                                                            {iniciais(e.nomeEmpresa)}
                                                          </span>}
                                                    <span>{e.nomeEmpresa || '—'}</span>
                                                </span>
                                            </span>
                                            <span className="lk-cell">
                                                {e.ramoAtividade
                                                    ? <span>{e.ramoAtividade}</span>
                                                    : <span className="lk-empty-value">não informado</span>}
                                            </span>
                                            <span className="lk-cell lk-muted ge-mono">
                                                {e.cnpj
                                                    ? <span>{comMascaraCnpj(e.cnpj)}</span>
                                                    : <span className="lk-empty-value">não informado</span>}
                                            </span>
                                            <span className="lk-cell lk-muted"><span>{resumoDias(dias)}</span></span>
                                            <RowActions>
                                                {/* Só o olho: não existe endpoint para apagar a própria
                                                    empresa, e um botão que não tem o que chamar mente. */}
                                                <RowAction icon={<LuEye size={14} />} label="Ver empresa"
                                                    onClick={() => abrirVer(e)} />
                                            </RowActions>
                                        </ListRow>
                                    );
                                })}
                        </div>
                    </div>

                    {!isLoading && filtradas.length === 0 && listaVazia}

                    {/* O rodapé só aparece quando há mais de uma página: paginar uma
                        linha é rodapé que ocupa espaço para não fazer nada. */}
                    {!isLoading && filtradas.length > porPagina && rodape}
                </div>
                )}
            </div>

            <RecordPanel
                open={!!aberta}
                onClose={fechar}
                squareAvatar
                initials={iniciais(aberta?.nomeEmpresa || '')}
                title={modo === 'editar'
                    ? (rascunho.nomeEmpresa.trim() || 'Empresa')
                    : (aberta?.nomeEmpresa || 'Empresa')}
                subtitle={aberta?.ramoAtividade || 'Sem ramo de atividade'}
                editing={aba === 'dados' && modo === 'editar'}
                onBack={() => setModo('ver')}
                tabs={[
                    { id: 'dados', label: 'Dados' },
                    { id: 'parametros', label: 'Parâmetros', icon: <LuSlidersHorizontal size={13} /> },
                ]}
                activeTab={aba}
                onTabChange={id => { setAba(id as Aba); setModo('ver'); setErro(''); }}
                footer={aba === 'dados' ? (
                    <>
                        {/* Sem "Excluir": a empresa é o próprio inquilino do sistema —
                            apagá-la aqui não existe na API nem faria sentido. */}
                        <button type="button" className="rp-btn" disabled={salvando}
                            onClick={() => setModo('ver')}>Cancelar</button>
                        <button type="button" className="rp-btn is-primary" onClick={salvar} disabled={salvando}>
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </button>
                    </>
                ) : undefined}
            >
                {aba === 'parametros' && (
                    <>
                        <PanelSection title="Vendas">
                            <div className="ge-par">
                                <div className="ge-par-h">
                                    <span className="ge-par-t">Utiliza tarefas padrão</span>
                                    <label className="rp-switch">
                                        <input type="checkbox" aria-label="Utiliza tarefas padrão"
                                            checked={parametros?.usaTarefasPadrao ?? false}
                                            disabled={salvandoParam || !parametros}
                                            onChange={e => alternarParametro({ usaTarefasPadrao: e.target.checked })} />
                                        <span className="rp-switch-track" /><span className="rp-switch-thumb" />
                                    </label>
                                </div>
                                <p className="ge-par-d">
                                    Cada oportunidade ganha um próximo passo ao entrar na etapa, com prazo e desfechos que movem o funil.
                                    {parametros?.temTrilho
                                        ? <> Trilho em uso: <b>{nomeTrilho ?? 'da empresa'}</b> ({parametros.etapasComTarefa} {parametros.etapasComTarefa === 1 ? 'etapa' : 'etapas'}).</>
                                        : <> Ainda sem trilho: ao ligar, você escolhe um na biblioteca.</>}
                                </p>
                                {parametros && !parametros.diasUteisCadastrados && (
                                    <p className="ge-alerta"><LuInfo size={13} /> Sem dias de funcionamento cadastrados, os prazos contam de segunda a sexta.</p>
                                )}
                                <Link className="ge-par-lnk" to="/tarefas-padrao">
                                    {parametros?.temTrilho ? 'Configurar tarefas padrão' : 'Ver a biblioteca de trilhos'} <LuArrowRight size={13} />
                                </Link>
                            </div>
                        </PanelSection>
                        <PanelSection title="Atendimento">
                            <div className="ge-par">
                                <div className="ge-par-h">
                                    <span className="ge-par-t">Mostrar o nome do atendente</span>
                                    <label className="rp-switch">
                                        <input type="checkbox" aria-label="Mostrar o nome do atendente"
                                            checked={parametros?.mostrarNomeDoAtendente ?? true}
                                            disabled={salvandoParam || !parametros}
                                            onChange={e => alternarParametro({ mostrarNomeDoAtendente: e.target.checked })} />
                                        <span className="rp-switch-track" /><span className="rp-switch-thumb" />
                                    </label>
                                </div>
                                <p className="ge-par-d">
                                    As respostas enviadas pelo chat chegam no WhatsApp do cliente com o nome de quem atendeu em negrito,
                                    na primeira linha. Desligado, o cliente recebe só o texto. No Instagram o nome nunca vai.
                                </p>
                            </div>
                        </PanelSection>
                    </>
                )}

                {aba === 'dados' && modo === 'ver' && aberta && (
                    <>
                        <div className="rp-actions is-inline">
                            <button type="button" className="rp-btn is-primary" onClick={() => abrirEdicao(aberta)}>
                                <LuPencil size={13} /> Editar
                            </button>
                        </div>
                        <PanelSection title="Identidade">
                            <div className="ge-id">
                                {mostrarLogo
                                    ? <img className="ge-logo" src={logoUrl!} alt=""
                                        onError={() => setLogoFalhou(true)} />
                                    : <span className="ge-logo is-vazia" aria-hidden="true">
                                        {iniciais(aberta.nomeEmpresa)}
                                      </span>}
                                <div className="ge-id-txt">
                                    <input ref={refArquivo} type="file" accept="image/*" hidden
                                        onChange={escolherArquivo} />
                                    <button type="button" className="rp-add" disabled={enviandoLogo}
                                        onClick={() => refArquivo.current?.click()}>
                                        <LuCamera size={13} /> {mostrarLogo ? 'Trocar logo' : 'Enviar logo'}
                                    </button>
                                    <p className="ge-id-hint">PNG ou JPG, de 64px a 2000px.</p>
                                </div>
                            </div>

                            <div className="ge-id ge-cor">
                                {aberta.corMarca
                                    ? <span className="ge-cor-amostra" style={{ backgroundColor: aberta.corMarca }} aria-hidden="true" />
                                    : <span className="ge-cor-amostra is-vazia" aria-hidden="true" />}
                                <div className="ge-id-txt">
                                    <div className="ge-cor-acoes">
                                        <span className="ge-cor-nome">
                                            Cor da marca{aberta.corMarca && <> · <span className="ge-mono">{aberta.corMarca}</span></>}
                                        </span>
                                        <input ref={refCor} type="color" className="ge-cor-input" tabIndex={-1} aria-hidden="true"
                                            defaultValue={aberta.corMarca ?? '#00275e'} key={aberta.corMarca ?? 'sem-cor'} />
                                        <button type="button" className="rp-add" disabled={gravandoCor}
                                            onClick={() => refCor.current?.click()}>
                                            <LuPalette size={13} /> {aberta.corMarca ? 'Trocar cor' : 'Escolher cor'}
                                        </button>
                                        {aberta.corMarca && (
                                            <button type="button" className="rp-add ge-cor-tirar" disabled={gravandoCor}
                                                onClick={() => gravarCor(null)}>
                                                Tirar
                                            </button>
                                        )}
                                    </div>
                                    <p className="ge-id-hint">
                                        Vai no PDF das propostas, orçamentos e contratos, e na página de captura quando ela não tem cor própria.
                                    </p>
                                </div>
                            </div>
                        </PanelSection>

                        <PanelSection title="Dados gerais">
                            <div className="rp-kv">
                                <span className="rp-k">Nome</span>
                                {aberta.nomeEmpresa
                                    ? <span className="rp-v">{aberta.nomeEmpresa}</span>
                                    : <span className="rp-v is-empty">não informado</span>}
                                <span className="rp-k">Ramo</span>
                                {aberta.ramoAtividade
                                    ? <span className="rp-v">{aberta.ramoAtividade}</span>
                                    : <span className="rp-v is-empty">não informado</span>}
                                <span className="rp-k">CNPJ</span>
                                {aberta.cnpj
                                    ? <span className="rp-v ge-mono">{comMascaraCnpj(aberta.cnpj)}</span>
                                    : <span className="rp-v is-empty">não informado</span>}
                            </div>
                        </PanelSection>

                        <PanelSection title="Dias de funcionamento">
                            <div className="ge-dias">
                                {DIAS.map(d => (
                                    <span key={d.key} className={`ge-dia ${dias[d.key] ? 'is-on' : ''}`}
                                        title={d.nome}>
                                        {d.abbr}
                                    </span>
                                ))}
                            </div>
                            <p className="ge-hint">
                                {resumoDias(dias)}
                                {ativos.length > 0 && ` · ${ativos.length} ${ativos.length === 1 ? 'dia' : 'dias'} por semana`}
                            </p>
                            <p className="ge-hint">
                                Base de cálculo dos dias úteis em metas e projeções.
                            </p>
                            {ativos.length === 0 && (
                                <p className="ge-alerta">
                                    <LuInfo size={13} />
                                    Sem nenhum dia útil, as projeções de meta ficam sem base de cálculo.
                                </p>
                            )}
                        </PanelSection>
                    </>
                )}

                {aba === 'dados' && modo === 'editar' && (
                    <>
                        <PanelSection title="Dados gerais">
                            <div className="rp-field">
                                <label className="rp-label" htmlFor="ge-nome">Nome da empresa</label>
                                <input id="ge-nome" className={`rp-input ${erro ? 'has-error' : ''}`} type="text" autoFocus
                                    value={rascunho.nomeEmpresa}
                                    onChange={e => { setRascunho(r => ({ ...r, nomeEmpresa: e.target.value })); setErro(''); }} />
                                {erro && <span className="rp-error">{erro}</span>}
                            </div>

                            <div className="rp-field">
                                <label className="rp-label" htmlFor="ge-ramo">Ramo de atividade</label>
                                <input id="ge-ramo" className="rp-input" type="text" placeholder="Ex.: Varejo"
                                    value={rascunho.ramoAtividade}
                                    onChange={e => setRascunho(r => ({ ...r, ramoAtividade: e.target.value }))} />
                            </div>

                            <div className="rp-field ge-w-cnpj">
                                <label className="rp-label" htmlFor="ge-cnpj">CNPJ</label>
                                <input id="ge-cnpj" className="rp-input" type="text" inputMode="numeric"
                                    placeholder="00.000.000/0000-00"
                                    value={comMascaraCnpj(rascunho.cnpj)}
                                    onChange={e => setRascunho(r => ({ ...r, cnpj: soDigitos(e.target.value) }))} />
                            </div>
                        </PanelSection>

                        <PanelSection title="Dias de funcionamento">
                            <div className="ge-dias">
                                {DIAS.map(d => (
                                    <button key={d.key} type="button"
                                        className={`ge-dia ${diasRascunho[d.key] ? 'is-on' : ''}`}
                                        aria-pressed={diasRascunho[d.key]}
                                        aria-label={d.nome}
                                        disabled={salvando}
                                        onClick={() => alternarDia(d.key)}>
                                        {d.abbr}
                                    </button>
                                ))}
                            </div>
                            <p className="ge-hint">
                                {resumoDias(diasRascunho)}
                                {ativos.length > 0 && ` · ${ativos.length} ${ativos.length === 1 ? 'dia' : 'dias'} por semana`}
                            </p>
                            {ativos.length === 0 && (
                                <p className="ge-alerta">
                                    <LuInfo size={13} />
                                    Sem nenhum dia útil, as projeções de meta ficam sem base de cálculo.
                                </p>
                            )}
                        </PanelSection>
                    </>
                )}
            </RecordPanel>

            <EscolherTrilhoModal
                aberta={bibliotecaAberta}
                onFechar={() => setBibliotecaAberta(false)}
                onEscolher={escolherTrilho}
            />

            {arquivoPendente && (
                <LogoCropModal
                    file={arquivoPendente}
                    minAspect={LOGO_MIN_ASPECT}
                    maxAspect={LOGO_MAX_ASPECT}
                    onConfirm={confirmarCorte}
                    onCancel={() => setArquivoPendente(null)}
                />
            )}
        </div>
    );
};

export default GerenciarEmpresaPage;
